import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { User } from '@classes/licence/user';
import { UserRole } from '@classes/licence/user-role';
import { Language } from '@classes/language';
import { LicenceService } from '@services/licence.service';
import { UiService } from '@services/ui.service';
import { AuthService } from '@services/auth.service';
import { LicenceRoleType } from '@enums/licence-role-type.enum';
import { OrganismService } from '@services/organism.service';
import { Region } from '@classes/region';

@Component({
    selector: 'app-user-edit',
    templateUrl: './user-edit.component.html',
    styleUrls: ['./user-edit.component.scss']
})
export class UserEditComponent implements OnInit, OnDestroy {
    @Input() user: User;
    @Output() editFinished = new EventEmitter<void>();

    private loadingSub: Subscription;

    userForm: FormGroup;
    rolesList: UserRole[];
    languagesList: Language[];
    regionsList: Region[];

    isLoading = true;
    userNameRexExp = new RegExp('^[a-zA-Z0-9._-]{3,50}$');

    constructor(
        private fb: FormBuilder,
        private licenceSrv: LicenceService,
        private uiSrv: UiService,
        private authSrv: AuthService,
        private organismSrv: OrganismService
    ) { }

    ngOnInit() {
        this.initSubscriptions();
    }

    onSubmit(): void {
        this.isLoading = true;
        if (this.user) {
            this.updateUser();
        } else {
            this.createUser();
        }
    }

    onCancel(): void {
        this.editFinished.emit();
    }

    private updateUser(): void {
        const userCopy: User = JSON.parse(JSON.stringify(this.user));
        const roleCode = this.userForm.value.role || this.user.role.code;

        userCopy.username = this.userForm.value.username.trim();
        userCopy.email = this.userForm.value.email.trim().toLowerCase();
        userCopy.language = this.languagesList.find(l => l.code === this.userForm.value.lang);
        userCopy.role = this.rolesList.find(r => r.code === roleCode);
        userCopy.provider = this.canSelectProvider ? this.regionsList.find(r => r.provider.code === this.userForm.value.provider)?.provider : null;

        this.licenceSrv.putUser(userCopy).subscribe(() => {
            this.licenceSrv.get(this.licenceSrv.getSelectedLicence().code, false)
            .subscribe(() => this.isLoading = false);
        });
    }

    private createUser(): void {
        this.user = new User(
            this.userForm.value.username,
            this.userForm.value.email,
            this.languagesList.find(l => l.code === this.userForm.value.lang),
            this.rolesList.find(r => r.code === this.userForm.value.role),
            this.canSelectProvider ? this.regionsList.find(r => r.provider.code === this.userForm.value.provider)?.provider : null);

        this.licenceSrv.postUser(this.user)
            .subscribe(
                () => this.isLoading = false,
                () => {
                    this.user = null;
                    this.isLoading = false;
                });
    }

    private initSubscriptions(): void {
        this.licenceSrv.getDropdownsContent()
            .subscribe(dropdownsContent => {
                this.languagesList = dropdownsContent.languages;
                // filter dropdown list on roles with quota not full
                // don't filter the selected user's role to show it even if the quota is full
                this.rolesList = this.licenceSrv.getSelectedLicence()
                    .roles
                    .filter(r => r.userQuota.value < r.userQuota.max || (this.user && this.user.role.code === r.code));

                this.initForm();
                this.isLoading = false;
            });

        this.organismSrv.getRegions(false)
            .subscribe(regions => {
                this.regionsList = regions;
            });

        this.loadingSub = this.uiSrv.loadingStateChanged
            .subscribe(loadingState => {
                this.isLoading = loadingState;
            });
    }

    private initForm(): void {
        const isRoleDisabled = this.user && !this.authSrv.getAuthUser().isLicencesManager;
        let username = "";
        let email = "";
        let lang = "";
        let role = "";
        let provider = "";

        if (this.user) {
            username = this.user.username;
            email = this.user.email;
            lang = this.user.language.code;
            role = this.user.role.code;
            provider = this.user.provider?.code;
        }

        this.userForm = this.fb.group({
            username: [username, [Validators.required, this.GetUserNameValidator(username)]],
            email: [email, [Validators.required, Validators.email]],
            lang: [lang, Validators.required],
            role: [{ value: role, disabled: isRoleDisabled }, Validators.required],
            provider: [provider]
        });
    }

    private GetUserNameValidator(oldUserName: string) {
        return (formControl: FormControl) => {
            const userName: string = formControl.value;
            if (oldUserName && userName === oldUserName)
                return null;
            if (this.userNameRexExp.test(userName))
                return null;
            return "error";
        }
    }

    ngOnDestroy(): void {
        if (this.loadingSub) {
            this.loadingSub.unsubscribe();
        }
    }

    get canSelectProvider(): boolean {
        return this.userForm.value.role === LicenceRoleType.bank ||
            this.userForm.value.role === LicenceRoleType.bzd ||
            this.userForm.value.role === LicenceRoleType.bko;
    }
}
